/**
 * EidHomeBanner — Eid Al Fitr festive banner for the home page.
 *
 * Usage:
 *   <EidHomeBanner />
 *
 * Design: bright, joyful, celebratory — continuously rising/floating balloons,
 * confetti, firework bursts, rainbow ribbon. Built for teenage students.
 */

import { cn } from "@design-system/lib/utils";

// ── Types ─────────────────────────────────────────────────────────────────────

type BalloonConfig = {
  color: string;
  highlight: string;
  left: string;
  /** Total rise duration in seconds */
  duration: number;
  /** Animation start delay in seconds — staggers the loop so they don't all appear at once */
  delay: number;
  /** Drift sway duration (horizontal wobble) */
  driftDuration: number;
  size: "sm" | "md" | "lg";
};

// ── Balloon SVG ───────────────────────────────────────────────────────────────

/**
 * A single balloon that continuously floats upward and gently sways left/right.
 *
 * Example:
 *   <FloatingBalloon color="#f87171" highlight="#fca5a5" left="20%" duration={6} delay={0} driftDuration={3} size="md" />
 */
function FloatingBalloon({
  color,
  highlight,
  left,
  duration,
  delay,
  driftDuration,
  size,
}: BalloonConfig) {
  const dims =
    size === "sm"
      ? { w: 28, h: 40 }
      : size === "lg"
        ? { w: 48, h: 68 }
        : { w: 36, h: 52 };

  return (
    // Outer wrapper: handles the upward rise
    <div
      className="absolute bottom-0 will-change-transform"
      style={{
        left,
        width: dims.w,
        animation: `balloon-rise ${duration}s linear ${delay}s infinite`,
      }}
    >
      {/* Inner wrapper: handles the horizontal drift independently */}
      <div
        style={{
          animation: `balloon-drift ${driftDuration}s ease-in-out ${delay * 0.5}s infinite`,
        }}
      >
        <svg
          viewBox="0 0 40 58"
          width={dims.w}
          height={dims.h}
          aria-hidden="true"
        >
          {/* Balloon body */}
          <ellipse cx="20" cy="20" rx="16" ry="18" fill={color} />
          {/* Shine spot */}
          <ellipse cx="13" cy="12" rx="4.5" ry="5.5" fill={highlight} opacity="0.45" />
          {/* Small shine dot */}
          <circle cx="24" cy="10" r="2" fill={highlight} opacity="0.3" />
          {/* Knot */}
          <path d="M17 37 Q20 40 23 37" stroke={color} strokeWidth="1.5" fill="none" />
          <circle cx="20" cy="38.5" r="1.8" fill={color} />
          {/* String */}
          <path
            d="M20 40.5 Q17 46 20 50 Q23 54 20 58"
            stroke="#94a3b899"
            strokeWidth="1.2"
            fill="none"
          />
        </svg>
      </div>
    </div>
  );
}

// ── FireworkBurst ────────────────────────────────────────────────────────────

function FireworkBurst({
  color,
  className,
}: {
  color: string;
  className?: string;
}) {
  return (
    <svg viewBox="0 0 50 50" className={cn("flex-shrink-0", className)} aria-hidden="true">
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        return (
          <line
            key={i}
            x1="25" y1="25"
            x2={25 + Math.cos(rad) * 20}
            y2={25 + Math.sin(rad) * 20}
            stroke={color}
            strokeWidth="2.5"
            strokeLinecap="round"
            opacity="0.85"
          />
        );
      })}
      <circle cx="25" cy="25" r="4" fill={color} opacity="0.9" />
    </svg>
  );
}

// ── ConfettiPiece ─────────────────────────────────────────────────────────────

function ConfettiPiece({ className, rotate = 0 }: { className?: string; rotate?: number }) {
  return (
    <div
      className={cn("rounded-sm", className)}
      style={{ transform: `rotate(${rotate}deg)` }}
    />
  );
}

// ── Star ─────────────────────────────────────────────────────────────────────

function Star({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={cn("flex-shrink-0", className)} aria-hidden="true">
      <polygon
        points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"
        fill="currentColor"
      />
    </svg>
  );
}

// ── Balloon Configs ───────────────────────────────────────────────────────────

// Spread across banner width with varied sizes, speeds, and delays so the
// screen always has a few balloons mid-flight at any given moment.
// On mobile we cap all sizes to "sm" to keep balloons subtle and not overwhelming
const BALLOONS: BalloonConfig[] = [
  { color: "#f87171", highlight: "#fecaca", left: "4%",  duration: 7,   delay: 0,    driftDuration: 3.2, size: "sm" },
  { color: "#34d399", highlight: "#a7f3d0", left: "14%", duration: 9,   delay: 2,    driftDuration: 4,   size: "sm" },
  { color: "#fbbf24", highlight: "#fde68a", left: "25%", duration: 6.5, delay: 1,    driftDuration: 2.8, size: "md" },
  { color: "#a78bfa", highlight: "#ddd6fe", left: "36%", duration: 8,   delay: 3.5,  driftDuration: 3.5, size: "sm" },
  { color: "#fb923c", highlight: "#fed7aa", left: "47%", duration: 7.5, delay: 0.5,  driftDuration: 3,   size: "sm" },
  { color: "#38bdf8", highlight: "#bae6fd", left: "58%", duration: 9.5, delay: 2.5,  driftDuration: 4.2, size: "md" },
  { color: "#f472b6", highlight: "#fbcfe8", left: "68%", duration: 6,   delay: 4,    driftDuration: 2.5, size: "sm" },
  { color: "#4ade80", highlight: "#bbf7d0", left: "78%", duration: 8.5, delay: 1.5,  driftDuration: 3.8, size: "sm" },
  { color: "#facc15", highlight: "#fef08a", left: "88%", duration: 7,   delay: 3,    driftDuration: 3.1, size: "md" },
];

// ── Main Component ─────────────────────────────────────────────────────────────

export function EidHomeBanner() {
  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-2xl",
        "bg-gradient-to-br from-emerald-50 via-white to-teal-50",
        "pb-8 sm:pb-14",
        "border border-emerald-200 shadow-xl shadow-emerald-100/60",
      )}
      // Mobile: shorter so balloons don't dominate. sm+: more room.
      style={{ minHeight: "clamp(180px, 30vw, 280px)" }}
      dir="rtl"
    >
      {/* Subtle dot pattern overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(circle, #10b981 1px, transparent 1px), radial-gradient(circle, #f59e0b 1px, transparent 1px)",
          backgroundSize: "30px 30px, 60px 60px",
          backgroundPosition: "0 0, 15px 15px",
        }}
      />

      {/* Continuously floating balloon layer — behind all content */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {BALLOONS.map((b, i) => (
          <FloatingBalloon key={i} {...b} />
        ))}
      </div>

      {/* Colored ribbon top bar */}
      <div className="flex h-3 w-full overflow-hidden">
        {["bg-emerald-500", "bg-amber-400", "bg-teal-500", "bg-rose-400", "bg-emerald-400", "bg-purple-400", "bg-amber-500", "bg-teal-400"].map(
          (color, i) => <div key={i} className={cn("flex-1", color)} />,
        )}
      </div>

      {/* Firework bursts — only on sm+ to keep mobile clean */}
      <FireworkBurst
        color="#10b981"
        className="absolute left-[10%] top-6 hidden h-7 w-7 animate-pulse opacity-50 sm:block sm:left-[18%] sm:top-8 sm:h-8 sm:w-8 sm:opacity-60"
      />
      <FireworkBurst
        color="#f59e0b"
        className="absolute right-[10%] top-6 hidden h-7 w-7 animate-pulse opacity-50 sm:block sm:right-[18%] sm:top-8 sm:h-8 sm:w-8 sm:opacity-60 [animation-delay:0.6s]"
      />
      <FireworkBurst
        color="#8b5cf6"
        className="absolute left-[38%] top-5 hidden h-5 w-5 animate-pulse opacity-40 sm:block sm:top-6 sm:h-6 sm:w-6 [animation-delay:1s]"
      />
      <FireworkBurst
        color="#f43f5e"
        className="absolute right-[38%] top-5 hidden h-5 w-5 animate-pulse opacity-40 sm:block sm:top-6 sm:h-6 sm:w-6 [animation-delay:0.3s]"
      />

      {/* Confetti pieces — hidden on mobile to reduce noise */}
      <ConfettiPiece className="absolute left-[28%] top-24 hidden h-2.5 w-1.5 animate-bounce bg-rose-400 sm:block [animation-delay:0.2s]" rotate={20} />
      <ConfettiPiece className="absolute left-[45%] top-28 hidden h-2 w-1 animate-bounce bg-amber-400 sm:block [animation-delay:0.8s]" rotate={-35} />
      <ConfettiPiece className="absolute right-[28%] top-24 hidden h-2.5 w-1.5 animate-bounce bg-teal-400 sm:block [animation-delay:0.5s]" rotate={45} />
      <ConfettiPiece className="absolute right-[45%] top-28 hidden h-2 w-1 animate-bounce bg-purple-400 sm:block [animation-delay:1.1s]" rotate={-15} />

      {/* Center content — above the balloon layer */}
      <div className="relative z-10 flex flex-col items-center gap-2 pt-5 sm:gap-3 sm:pt-8">
        {/* Star row — smaller on mobile */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          <Star className="h-3 w-3 animate-spin text-amber-400 sm:h-4 sm:w-4 [animation-duration:6s]" />
          <Star className="h-4 w-4 animate-pulse text-emerald-500 sm:h-5 sm:w-5" />
          <Star className="h-5 w-5 text-amber-400 sm:h-6 sm:w-6 [animation-duration:4s]" />
          <Star className="h-4 w-4 animate-pulse text-emerald-500 sm:h-5 sm:w-5 [animation-delay:0.5s]" />
          <Star className="h-3 w-3 animate-spin text-amber-400 sm:h-4 sm:w-4 [animation-duration:6s] [animation-delay:1s]" />
        </div>

        {/* Main Arabic headline — text-3xl on mobile, text-5xl on sm+ */}
        <h2
          className={cn(
            "animate-shimmer bg-[length:200%_auto] bg-clip-text text-transparent",
            "bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-600",
            "text-3xl font-bold tracking-wide sm:text-5xl sm:tracking-widest",
          )}
          style={{ lineHeight: "1.5" }}
        >
          عيد الفطر المبارك
        </h2>

        {/* Emoji celebration row — smaller on mobile */}
        <p className="text-lg sm:text-2xl">🎉 🎊 🎈 🎊 🎉</p>

        {/* Sub-line */}
        <p
          className="px-4 text-center text-xs tracking-wide text-emerald-700/80 sm:px-0 sm:text-base"
          dir="rtl"
        >
          كل عام وأنتم بخير — تقبّل الله منا ومنكم صالح الأعمال
        </p>

        {/* Decorative divider */}
        <div className="mt-0.5 flex items-center gap-2 sm:mt-1 sm:gap-3">
          <div className="h-px w-12 bg-gradient-to-r from-transparent to-emerald-400/60 sm:w-24" />
          <Star className="h-3 w-3 animate-spin text-amber-400 [animation-duration:8s]" />
          <div className="h-px w-12 bg-gradient-to-l from-transparent to-emerald-400/60 sm:w-24" />
        </div>
      </div>
    </div>
  );
}
